/**
 * The board's controls, in pieces each layout arranges its own way: the
 * desktop puts them all in the left column (`Toolbar`, below); a phone puts
 * the tools and undo in a strip under the board and the rest in its sheet.
 */

import type { JSX } from 'react';
import { term } from '../core/index.ts';
import type { BoardApi, Tool } from './useBoard.ts';
import { FormationControls } from './FormationControls.tsx';
import type { FormationsApi } from './useFormations.ts';

interface ToolSpec {
  readonly id: Tool;
  readonly icon: string;
  readonly termKey: string;
  readonly fallback: string;
  readonly shortcut: string;
}

const TOOLS: readonly ToolSpec[] = [
  { id: 'select', icon: '✥', termKey: 'tool.select', fallback: 'Select & move', shortcut: 'V' },
  { id: 'move', icon: '↗', termKey: 'tool.move', fallback: 'Movement', shortcut: 'A' },
  { id: 'pass', icon: '⇢', termKey: 'tool.pass', fallback: 'Pass', shortcut: 'P' },
  { id: 'shot', icon: '➤', termKey: 'tool.shot', fallback: 'Shot', shortcut: 'S' },
  { id: 'freehand', icon: '✎', termKey: 'tool.freehand', fallback: 'Freehand', shortcut: 'F' },
  { id: 'erase', icon: '⌫', termKey: 'tool.erase', fallback: 'Erase annotation', shortcut: 'E' },
];

interface ToolButtonsProps {
  readonly api: BoardApi;
  /** Icons only, sized for a finger: the phone's tool strip. */
  readonly compact?: boolean;
}

export function ToolButtons({ api, compact = false }: ToolButtonsProps): JSX.Element {
  return (
    <div className={compact ? 'tool-strip__tools' : 'toolbar__tools'}>
      {TOOLS.map((spec) => {
        // A group can only be moved; the other tools wait for the selection to go.
        const blocked = api.multiSelection && spec.id !== 'select';
        const label = term(api.pack, spec.termKey, spec.fallback);
        return (
          <button
            key={spec.id}
            type="button"
            className={`tool ${compact ? 'tool--compact' : ''} ${api.tool === spec.id ? 'tool--active' : ''}`}
            data-tutorial={`tool-${spec.id}`}
            onClick={() => api.setTool(spec.id)}
            disabled={blocked}
            title={blocked ? 'Clear the selection first' : compact ? label : `${label} (${spec.shortcut})`}
            // With the label hidden, the button still has to say what it is.
            aria-label={compact ? label : undefined}
            aria-pressed={api.tool === spec.id}
          >
            <span className="tool__icon" aria-hidden="true">
              {spec.icon}
            </span>
            {!compact && <span className="tool__label">{label}</span>}
            {!compact && <span className="tool__key">{spec.shortcut}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function InkControls({ api }: { readonly api: BoardApi }): JSX.Element {
  return (
    <>
      <div className="swatches">
        {api.pack.tools.palette.map((swatch) => (
          <button
            key={swatch}
            type="button"
            className={`swatch ${api.color === swatch ? 'swatch--active' : ''}`}
            style={{ background: swatch }}
            onClick={() => api.setColor(swatch)}
            title={swatch}
            aria-label={`Ink colour ${swatch}`}
          />
        ))}
      </div>
      <label className="field">
        <span>Width</span>
        <input
          type="range"
          min={0.003}
          max={0.02}
          step={0.001}
          value={api.strokeWidth}
          onChange={(event) => api.setStrokeWidth(Number(event.target.value))}
        />
      </label>
      <button type="button" className="btn btn--ghost" onClick={api.clearAnnotations}>
        Clear annotations
      </button>
    </>
  );
}

interface SetupControlsProps {
  readonly api: BoardApi;
  readonly formations: FormationsApi;
  /** A phone is tapped, not clicked: only the hint's wording changes. */
  readonly phone?: boolean;
}

export function SetupControls({ api, formations, phone = false }: SetupControlsProps): JSX.Element {
  return (
    <>
      <FormationControls api={api} formations={formations} />
      {api.rosterMax !== null && (
        <div className="roster" data-tutorial="roster">
          {/* One row per team: its cap, its name and count, − and +. + adds
              the next position of the standard lineup; positions and
              numbers are edited on the board with a double-click. */}
          {api.pack.teams.map((team) => (
            <div className="roster__team" key={team.id}>
              <span
                className="roster__cap"
                style={{ background: team.color, borderColor: team.outline }}
                aria-hidden="true"
              />
              <span className="roster__name">{team.name}</span>
              <span className="roster__count">
                {api.rosterCountFor(team.id)} / {api.rosterMax}
              </span>
              <button
                type="button"
                className="btn roster__step"
                aria-label={`Remove a player from ${team.name}`}
                title={`Remove a player from ${team.name}`}
                disabled={!api.canRemovePlayer(team.id)}
                onClick={() => api.removePlayer(team.id)}
              >
                −
              </button>
              <button
                type="button"
                className="btn roster__step"
                aria-label={`Add a player to ${team.name}`}
                title={`Add a player to ${team.name}`}
                disabled={!api.canAddPlayer(team.id)}
                onClick={() => api.addPlayer(team.id)}
              >
                +
              </button>
            </div>
          ))}
          <p className="toolbar__hint">
            {phone
              ? "Double-tap a player's number or position on the board to change it."
              : "Double-click a player's number or position on the board to change it."}
          </p>
        </div>
      )}
      <label className="checkbox">
        <input
          type="checkbox"
          checked={api.showRoleLabels}
          onChange={(event) => api.setShowRoleLabels(event.target.checked)}
        />
        <span>Show position labels</span>
      </label>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={api.showVenueDetail}
          onChange={(event) => api.setShowVenueDetail(event.target.checked)}
        />
        <span>Show venue surround</span>
      </label>
    </>
  );
}

export interface ExportActions {
  readonly onExportPng: () => void;
  readonly onExportJson: () => void;
  readonly onImportJson: () => void;
}

export function ExportControls({ onExportPng, onExportJson, onImportJson }: ExportActions): JSX.Element {
  return (
    <>
      <div className="toolbar__row">
        <button type="button" className="btn" onClick={onExportPng}>
          PNG
        </button>
        <button type="button" className="btn" onClick={onExportJson}>
          JSON
        </button>
      </div>
      <button type="button" className="btn btn--ghost" onClick={onImportJson}>
        Open .json…
      </button>
    </>
  );
}

interface HistoryButtonsProps {
  readonly api: BoardApi;
  /** Arrows only, sized for a finger: the phone's tool strip. */
  readonly compact?: boolean;
}

export function HistoryButtons({ api, compact = false }: HistoryButtonsProps): JSX.Element {
  if (compact) {
    return (
      <div className="tool-strip__history">
        <button type="button" className="tool tool--compact" onClick={api.undoAction} disabled={!api.canUndo} aria-label="Undo" title="Undo">
          <span className="tool__icon" aria-hidden="true">↶</span>
        </button>
        <button type="button" className="tool tool--compact" onClick={api.redoAction} disabled={!api.canRedo} aria-label="Redo" title="Redo">
          <span className="tool__icon" aria-hidden="true">↷</span>
        </button>
      </div>
    );
  }
  return (
    <div className="toolbar__row">
      <button type="button" className="btn btn--ghost" onClick={api.undoAction} disabled={!api.canUndo}>
        ↶ Undo
      </button>
      <button type="button" className="btn btn--ghost" onClick={api.redoAction} disabled={!api.canRedo}>
        ↷ Redo
      </button>
    </div>
  );
}

/** The pool the pack describes, e.g. "Water polo · 25×20 m". */
export function PackHint({ api }: { readonly api: BoardApi }): JSX.Element {
  return (
    <p className="toolbar__hint">
      {api.pack.name} · {api.pack.surface.dimensions.length}×{api.pack.surface.dimensions.width}{' '}
      {api.pack.surface.dimensions.unit}
    </p>
  );
}

interface ToolbarProps extends ExportActions {
  readonly api: BoardApi;
  readonly formations: FormationsApi;
  /**
   * Exporting and opening files is developer work — a coach signs in and finds
   * their board, and the recording panel is how a play leaves the app. The
   * section is there when the page is opened with `?dev=1`, so a broken board
   * can still be asked for as JSON.
   */
  readonly developer: boolean;
}

/** The desktop's left column: every control, grouped. */
export function Toolbar({ api, formations, developer, ...exports }: ToolbarProps): JSX.Element {
  return (
    <aside className="toolbar">
      <section className="toolbar__group">
        <h2 className="toolbar__heading">Tools</h2>
        <ToolButtons api={api} />
      </section>

      <section className="toolbar__group">
        <h2 className="toolbar__heading">Ink</h2>
        <InkControls api={api} />
      </section>

      <section className="toolbar__group">
        <h2 className="toolbar__heading">Setup</h2>
        <SetupControls api={api} formations={formations} />
      </section>

      {developer && (
        <section className="toolbar__group">
          <h2 className="toolbar__heading">Export</h2>
          <ExportControls {...exports} />
        </section>
      )}

      <section className="toolbar__group toolbar__group--footer">
        <HistoryButtons api={api} />
        <PackHint api={api} />
      </section>
    </aside>
  );
}
